import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { UserSettingsService } from "../../services/userSettingsService";
import logger from "../../core/utils/logger";

// Async thunk for getting user settings
export const getUserSettings = createAsyncThunk(
    'userSettings/getUserSettings',
    async (_, { rejectWithValue }) => {
        try {
            const response = await UserSettingsService.getUserSettings();
            logger.log("Get user settings response:", response);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error getting user settings');
        }
    }
);

// Async thunk for saving user settings
export const saveUserSettings = createAsyncThunk(
    'userSettings/saveUserSettings',
    async (settings, { rejectWithValue }) => {
        try {
            const response = await UserSettingsService.saveUserSettings(settings);
            logger.log("Save user settings response:", response);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error saving user settings');
        }
    }
);

// Async thunk for getting organization settings (admin)
export const getOrganizationSettings = createAsyncThunk(
    'userSettings/getOrganizationSettings',
    async (_, { rejectWithValue }) => {
        try {
            const response = await UserSettingsService.getOrganizationSettings();
            logger.log("Get organization settings response:", response);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error getting organization settings');
        }
    }
);

const initialState = {
    loading: false,
    error: null,
    settings: {
        workerSortBy: "Name", // Default values
        workerSortOrder: "Asc",
        customWorkerOrder: null
    },
    organizationSettings: []
};

const userSettingsSlice = createSlice({
    name: 'userSettings',
    initialState,
    reducers: {
        // Synchronous action to update settings locally (for immediate UI feedback)
        updateSettingsLocally: (state, action) => {
            state.settings = { ...state.settings, ...action.payload };
        },
        clearSettings: (state) => {
            state.settings = initialState.settings;
            state.organizationSettings = [];
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        // Get user settings
        builder.addCase(getUserSettings.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getUserSettings.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.settings = action.payload;
        });
        builder.addCase(getUserSettings.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Save user settings
        builder.addCase(saveUserSettings.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(saveUserSettings.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.settings = action.payload;
        });
        builder.addCase(saveUserSettings.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Get organization settings
        builder.addCase(getOrganizationSettings.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getOrganizationSettings.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.organizationSettings = action.payload;
        });
        builder.addCase(getOrganizationSettings.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    }
});

export const { updateSettingsLocally, clearSettings } = userSettingsSlice.actions;
export default userSettingsSlice.reducer;
